/**
 * System scanner — the engine behind the `system-scanner` deployed-agent
 * template.
 *
 * `scanSystem()` sweeps the live collections (tickets, tasks, prospects,
 * accounts) and returns a ranked list of `ScanFinding`s: the overdue,
 * blocked, unowned-but-urgent, and stalled items a human should look at
 * first. It's a pure function over snapshots so it can run inside a
 * `useMemo` on the Dashboard, on a timer, or when the deployed scanner
 * agent ticks — no I/O of its own.
 *
 * The ranking is deliberately simple and explainable: severity bucket
 * first (high → medium → low), then by how overdue / how stale the item
 * is, so the top of the list is always the most pressing thing.
 */

import type {
  Account,
  BusinessDoc,
  Person,
  ProjectTask,
  Ticket,
} from '../db/types';

export type FindingSeverity = 'high' | 'medium' | 'low';

export type FindingSource =
  | 'ticket'
  | 'task'
  | 'prospect'
  | 'account'
  | 'document';

/**
 * One surfaced item. `source` + `entityId` let a UI deep-link back to
 * the underlying record; `sortKey` is an internal magnitude (days
 * overdue / stale) used only for ordering within a severity bucket.
 */
export interface ScanFinding {
  id: string;
  severity: FindingSeverity;
  /** Short bucket label, e.g. "Overdue ticket". */
  category: string;
  /** One-line headline, e.g. "TIQ-014 · Fix billing export". */
  title: string;
  /** Supporting detail, e.g. "Deadline was 3 days ago · unowned". */
  detail: string;
  source: FindingSource;
  entityId: string;
  /** Higher = more pressing within the same severity. */
  sortKey: number;
}

export interface ScanInput {
  tickets: Ticket[];
  tasks: ProjectTask[];
  people: Person[];
  accounts: Account[];
  /**
   * Proposals & contracts. Optional so the many lightweight call sites that
   * only care about tickets/tasks/people/accounts don't have to thread an
   * empty array; the document sweep simply produces nothing when omitted.
   */
  documents?: BusinessDoc[];
  /** Injectable clock for tests; defaults to now. */
  now?: number;
}

/** Aggregate counts by severity — handy for headline badges. */
export interface ScanSummary {
  total: number;
  high: number;
  medium: number;
  low: number;
}

export interface ScanResult {
  findings: ScanFinding[];
  summary: ScanSummary;
  /** ISO timestamp the scan ran. */
  scannedAt: string;
}

const DAY = 24 * 60 * 60 * 1000;

const SEVERITY_RANK: Record<FindingSeverity, number> = {
  high: 0,
  medium: 1,
  low: 2,
};

/** Whole days between `iso` and `now` (positive = in the past). */
function daysAgo(iso: string, now: number): number {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return 0;
  return Math.floor((now - t) / DAY);
}

/** Days a plain (YYYY-MM-DD) date is past `now`, comparing on day start. */
function daysOverdue(dateStr: string, now: number): number {
  const due = Date.parse(dateStr);
  if (Number.isNaN(due)) return 0;
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  return Math.floor((startOfToday.getTime() - due) / DAY);
}

/** Whole days until a future (YYYY-MM-DD) date; negative once it's past. */
function daysUntil(dateStr: string, now: number): number {
  return -daysOverdue(dateStr, now);
}

function plural(n: number, unit: string): string {
  return `${n} ${unit}${n === 1 ? '' : 's'}`;
}

/** Was this deadline-ish date in the past? */
function isPast(dateStr: string | null | undefined, now: number): boolean {
  if (!dateStr) return false;
  return daysOverdue(dateStr, now) > 0;
}

const TERMINAL_TICKET: Ticket['status'][] = ['done', 'cancelled'];

/**
 * Sweep the system and return ranked findings.
 *
 * Buckets, in order of typical severity:
 *   - Overdue tickets (high if p0/p1, else medium)
 *   - Unowned high-priority open tickets (high for p0, medium for p1)
 *   - Blocked tickets (medium)
 *   - Stale in-progress tickets — started >5d ago, still moving (low)
 *   - Overdue tasks (high if urgent, medium if high, else low)
 *   - Blocked tasks (medium)
 *   - Unassigned urgent/high tasks (medium)
 *   - Prospects with an overdue follow-up (medium)
 *   - Stalled accounts — untouched >21d with workflows in flight (low)
 *   - Lapsed proposals/contracts — past validUntil, still sent/viewed (high)
 *   - Documents awaiting a client response/signature >7d (medium)
 */
export function scanSystem(input: ScanInput): ScanResult {
  const now = input.now ?? Date.now();
  const findings: ScanFinding[] = [];

  const push = (f: Omit<ScanFinding, 'id'>) =>
    findings.push({ ...f, id: `${f.source}:${f.entityId}:${f.category}` });

  // --- Tickets -------------------------------------------------------------
  for (const t of input.tickets) {
    if (TERMINAL_TICKET.includes(t.status)) continue;
    const urgent = t.priority === 'p0' || t.priority === 'p1';

    // Overdue deadline.
    if (isPast(t.deadline, now)) {
      const od = daysOverdue(t.deadline!, now);
      push({
        severity: urgent ? 'high' : 'medium',
        category: 'Overdue ticket',
        title: `${t.id} · ${t.title}`,
        detail: `Deadline ${plural(od, 'day')} ago · ${t.priority.toUpperCase()} · ${
          t.owner ? `owned by ${t.owner}` : 'unowned'
        }`,
        source: 'ticket',
        entityId: t.id,
        sortKey: od + (urgent ? 100 : 0),
      });
    }

    // Unowned but high-priority and still open/backlog.
    if (
      !t.owner &&
      urgent &&
      (t.status === 'open' || t.status === 'backlog')
    ) {
      push({
        severity: t.priority === 'p0' ? 'high' : 'medium',
        category: 'Unowned priority ticket',
        title: `${t.id} · ${t.title}`,
        detail: `${t.priority.toUpperCase()} · ${t.status} · no owner assigned`,
        source: 'ticket',
        entityId: t.id,
        sortKey: t.priority === 'p0' ? 90 : 60,
      });
    }

    // Blocked.
    if (t.status === 'blocked') {
      push({
        severity: 'medium',
        category: 'Blocked ticket',
        title: `${t.id} · ${t.title}`,
        detail: `Blocked · ${t.owner ? `owned by ${t.owner}` : 'unowned'}`,
        source: 'ticket',
        entityId: t.id,
        sortKey: 50,
      });
    }

    // Stale in-progress — work started but no recent movement.
    if (t.status === 'in-progress' && t.startedAt) {
      const stale = daysAgo(t.updatedAt, now);
      if (stale >= 5) {
        push({
          severity: 'low',
          category: 'Stalled ticket',
          title: `${t.id} · ${t.title}`,
          detail: `In progress, untouched ${plural(stale, 'day')} · ${
            t.owner ?? 'unowned'
          }`,
          source: 'ticket',
          entityId: t.id,
          sortKey: stale,
        });
      }
    }
  }

  // --- Tasks ---------------------------------------------------------------
  for (const task of input.tasks) {
    if (task.status === 'done') continue;

    // Overdue due date.
    if (isPast(task.dueDate, now)) {
      const od = daysOverdue(task.dueDate!, now);
      const sev: FindingSeverity =
        task.priority === 'urgent'
          ? 'high'
          : task.priority === 'high'
            ? 'medium'
            : 'low';
      push({
        severity: sev,
        category: 'Overdue task',
        title: task.title,
        detail: `Due ${plural(od, 'day')} ago · ${task.priority} · ${
          task.assignee ?? 'unassigned'
        }${task.accountName ? ` · ${task.accountName}` : ''}`,
        source: 'task',
        entityId: task.id,
        sortKey: od + (sev === 'high' ? 100 : sev === 'medium' ? 40 : 0),
      });
    }

    // Blocked.
    if (task.status === 'blocked') {
      push({
        severity: 'medium',
        category: 'Blocked task',
        title: task.title,
        detail: `Blocked · ${task.assignee ?? 'unassigned'}${
          task.accountName ? ` · ${task.accountName}` : ''
        }`,
        source: 'task',
        entityId: task.id,
        sortKey: 45,
      });
    }

    // Unassigned but high/urgent.
    if (
      !task.assignee &&
      (task.priority === 'urgent' || task.priority === 'high')
    ) {
      push({
        severity: 'medium',
        category: 'Unassigned task',
        title: task.title,
        detail: `${task.priority} priority · no assignee`,
        source: 'task',
        entityId: task.id,
        sortKey: task.priority === 'urgent' ? 55 : 35,
      });
    }
  }

  // --- Customers: Billing & renewal automation --------------------------------
  for (const p of input.people) {
    if (p.category !== 'customer') continue;
    if (p.status === 'churned') continue;

    // Past-due billing milestone: date < now and status not paid.
    if (p.billingDates) {
      for (const bd of p.billingDates) {
        if (isPast(bd.date, now)) {
          const status = bd.status ?? 'scheduled';
          if (status === 'paid') continue;
          const od = daysOverdue(bd.date, now);
          push({
            severity: 'high',
            category: 'Billing past due',
            title: `${p.name} · ${bd.label}`,
            detail: `Past due ${plural(od, 'day')}${bd.amount ? ` · $${bd.amount}` : ''}`,
            source: 'account',
            entityId: p.id,
            sortKey: od + 100,
          });
        }
      }

      // Billing milestone due within the next 7 days.
      for (const bd of p.billingDates) {
        if (!isPast(bd.date, now)) {
          const out = daysUntil(bd.date, now);
          if (out <= 7) {
            push({
              severity: 'medium',
              category: 'Billing due soon',
              title: `${p.name} · ${bd.label}`,
              detail: `Due in ${plural(out, 'day')}${bd.amount ? ` · $${bd.amount}` : ''}`,
              source: 'account',
              entityId: p.id,
              // Sooner = more pressing within the medium bucket.
              sortKey: 7 - out,
            });
          }
        }
      }
    }

    // Renewal date within the next 30 days.
    if (p.renewalDate && !isPast(p.renewalDate, now)) {
      const out = daysUntil(p.renewalDate, now);
      if (out <= 30) {
        push({
          severity: 'medium',
          category: 'Renewal approaching',
          title: p.name,
          detail: `Renewal in ${plural(out, 'day')}${
            p.accountValue ? ` · $${p.accountValue} ARR` : ''
          }`,
          source: 'account',
          entityId: p.id,
          // Sooner = more pressing within the medium bucket.
          sortKey: 30 - out,
        });
      }
    }
  }

  // --- Prospects -----------------------------------------------------------
  for (const p of input.people) {
    if (p.category !== 'prospect') continue;
    // Skip closed deals.
    if (p.stage === 'closed-won' || p.stage === 'closed-lost') continue;

    if (isPast(p.followUpDate, now)) {
      const od = daysOverdue(p.followUpDate!, now);
      push({
        severity: od >= 7 ? 'medium' : 'low',
        category: 'Overdue follow-up',
        title: p.name,
        detail: `Follow-up ${plural(od, 'day')} overdue${
          p.nextStep ? ` · ${p.nextStep}` : ''
        }`,
        source: 'prospect',
        entityId: p.id,
        sortKey: od,
      });
    }

    // Next-step discipline: an open deal with no scheduled follow-up AND no
    // named next step is a deal drifting on memory. Weighted by deal size so
    // the biggest unprotected pipeline floats to the top.
    if (!p.followUpDate && !p.nextStep) {
      const value = p.estimatedValue ?? 0;
      push({
        severity: value >= 50_000 ? 'medium' : 'low',
        category: 'No next step',
        title: p.name,
        detail: `Open ${p.stage ?? 'new'} deal${
          value ? ` worth $${Math.round(value / 1000)}k` : ''
        } with no follow-up scheduled and no next step — set one or let it slip`,
        source: 'prospect',
        entityId: p.id,
        sortKey: Math.round(value / 1000),
      });
    }
  }

  // --- Accounts ------------------------------------------------------------
  for (const a of input.accounts) {
    if (a.workflows.length === 0) continue;
    const stale = daysAgo(a.updatedAt, now);
    if (stale >= 21) {
      push({
        severity: 'low',
        category: 'Stalled account',
        title: a.name,
        detail: `No activity in ${plural(stale, 'day')} · ${plural(
          a.workflows.length,
          'workflow',
        )} in flight`,
        source: 'account',
        entityId: a.id,
        sortKey: stale,
      });
    }
  }

  // --- Documents: proposals & contracts in play ----------------------------
  // Only documents still in the client's court matter here: a `draft` hasn't
  // gone out, and accepted/signed/declined/expired are already resolved. We
  // watch the ones that were sent and not yet answered. Note this is the same
  // "in play" set that ContractsView counts as open pipeline value, so a
  // lapsed-but-unclosed doc inflates that figure until someone acts — exactly
  // what we want to surface.
  for (const d of input.documents ?? []) {
    if (d.status !== 'sent' && d.status !== 'viewed') continue;

    const noun = d.kind === 'contract' ? 'Contract' : 'Proposal';
    const awaiting = d.kind === 'contract' ? 'signature' : 'response';

    // Lapsed: the validUntil date has passed but the doc is still sitting in
    // a sent/viewed state. It can no longer close on its stated terms yet
    // still counts as open pipeline — needs a re-issue or an explicit close.
    if (isPast(d.validUntil, now)) {
      const od = daysOverdue(d.validUntil!, now);
      push({
        severity: 'high',
        category: 'Lapsed document',
        title: `${d.title} · ${d.entityName}`,
        detail: `${noun} expired ${plural(od, 'day')} ago · still ${d.status}${
          d.value ? ` · $${d.value}` : ''
        } — re-issue or close it`,
        source: 'document',
        entityId: d.id,
        sortKey: od + 100,
      });
      // A lapsed doc supersedes the softer "awaiting" finding below — one
      // row per document keeps the list honest and uncluttered.
      continue;
    }

    // Awaiting client: out the door but no movement in a while. Nudge it
    // before it goes cold (and eventually lapses into the case above).
    const stale = daysAgo(d.updatedAt, now);
    if (stale >= 7) {
      push({
        severity: 'medium',
        category: 'Awaiting client',
        title: `${d.title} · ${d.entityName}`,
        detail: `${noun} ${d.status} · awaiting ${awaiting} ${plural(
          stale,
          'day',
        )} · nudge the client`,
        source: 'document',
        entityId: d.id,
        sortKey: stale,
      });
    }
  }

  // --- Rank ----------------------------------------------------------------
  findings.sort((x, y) => {
    const bySev = SEVERITY_RANK[x.severity] - SEVERITY_RANK[y.severity];
    if (bySev !== 0) return bySev;
    return y.sortKey - x.sortKey;
  });

  const summary: ScanSummary = {
    total: findings.length,
    high: findings.filter((f) => f.severity === 'high').length,
    medium: findings.filter((f) => f.severity === 'medium').length,
    low: findings.filter((f) => f.severity === 'low').length,
  };

  return { findings, summary, scannedAt: new Date(now).toISOString() };
}
